import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTabPanelActive, useTabPanelVisible } from 'screens/Main/tabs/MyCards/components/TabbedView/components/TabPanel/TabPanel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import { Link, useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useGetMeQuery } from 'store/services/user/meApi';
import { toast } from 'react-toastify';
import InfiniteScroll from 'components/InfiniteScroll';
import TextInputField from 'components/TextInputField';
import AddCardsSearchListItem from './components/AddCardsSearchListItem';
import { HoverPreview } from './components/AddCardsSearchListItem/AddCardsSearchListItem';
import CardImageTooltip from 'components/CardLink/CardImageTooltip';
import { SegmentedToggle } from 'components/SegmentedToggle/SegmentedToggle';
import { cardImageInputsFromCard, getCardImageUrl } from 'utils/cardImageFallback';
import { AddConfirmation, LastAdd } from './components/AddConfirmation/AddConfirmation';
import { useDropdownMaxHeight } from './useDropdownMaxHeight';
import {
  AddSearchView,
  IMAGE_RESULTS_LIMIT,
  addSearchIsIdle,
  buildAddSearchArgs,
  readStoredAddSearchView,
  storeAddSearchView,
} from './addSearchArgs';
import { MTGCard } from 'types/interfaces/mtgCard';
import {
  useCardsSearchInfiniteQuery,
  useSearchConsolidatedInfiniteQuery,
  ConsolidatedCard
} from 'store/services/cards/search/cardsSearchApi';
import debounce from 'lodash.debounce';
import ImportCardsModal, { ImportInputMode } from 'components/ImportCardsModal/ImportCardsModal';
import { AddSetModal } from 'components/AddSetModal';
import { BulkManageAlertsModal } from 'components/BulkManageAlertsModal';
import { SortOrder } from 'types/interfaces/sort';
import './AddCards.css';
import { copyToClipboard } from 'utils/copyToClipboard';

/** The "Adding as" / "Accepting" pill and what it implies for each result row. */
export interface AddCardsEntryDefaults {
  /** `onDone` puts focus back in Card name when the editor closes. */
  renderPill: (onDone: () => void) => React.ReactNode;
  /** Secondary line for a Specific Set result row: what a click will add. */
  previewLine?: (card: MTGCard) => string | undefined;
  /** The settings part of the add confirmation, e.g. "NM · Non-foil · EN" (Fast Entry). */
  confirmationLine?: (card: MTGCard) => string | undefined;
}

/** What an owner's onCardAdd may hand back so the Add search can offer Undo. */
export interface AddCardResult {
  /** Removes exactly one copy of the add just made. */
  undo: () => Promise<void>;
}

interface Props {
  placeholder: string;
  title?: string;
  subtitle?: string;
  onCardAdd: (identifier: number | string, cardData?: any, searchMode?: 'any' | 'specific') => void | Promise<AddCardResult | void>;
  useOracleId?: boolean; // Flag to determine whether to use oracleId or cardId
  isHaveList?: boolean; // Flag to enforce specific set selection
  isWantList?: boolean; // Flag to identify want list for share functionality
  onDeleteAll?: () => void; // Callback for delete all functionality
  viewMode?: 'list' | 'comfortable' | 'compact';
  onViewModeChange?: (mode: 'list' | 'comfortable' | 'compact') => void;
  /** The magnifying glass: the page opens the Filters drawer on Card name. */
  onSearchClick?: () => void;
  // Sort props
  orderBy?: string;
  order?: SortOrder;
  onSortChange?: (sortKey: string, direction?: SortOrder) => void;
  // Trade filter props (have list only)
  tradeFilter?: 'all' | 'forTrade' | 'notForTrade';
  onTradeFilterChange?: (filter: 'all' | 'forTrade' | 'notForTrade') => void;
  onExportCsv?: () => void;
  isExporting?: boolean;
  entryDefaults?: AddCardsEntryDefaults;
}
export const AddCards = ({ placeholder, title = "Add cards", subtitle, onCardAdd, useOracleId = false, isHaveList = false, isWantList = false, onDeleteAll, viewMode = 'list', onViewModeChange, onSearchClick, orderBy, order, onSortChange, tradeFilter, onTradeFilterChange, onExportCsv, isExporting, entryDefaults }: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  // False while this Add box sits in a hidden My Cards tab (Asana 1219915395102217).
  const active = useTabPanelActive();
  const filterInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Card-art preview shown to the right of the printings list on row hover.
  const [hoveredPreview, setHoveredPreview] = useState<HoverPreview | null>(null);
  const [previewPos, setPreviewPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Asana 1219947685097226: the overlay fills to the viewport bottom (max-height, not height).
  const overlayMaxHeight = useDropdownMaxHeight(overlayRef, Boolean(search && debouncedSearch));
  const [filterText, setFilterText] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  // Fast Entry (Asana 1219423341660540). The lock is component state: it lasts
  // until reload and is never saved. searchAllOnce drops it for ONE search.
  const [lockedFilter, setLockedFilter] = useState<string | null>(null);
  const [searchAllOnce, setSearchAllOnce] = useState(false);
  const [resultsView, setResultsView] = useState<AddSearchView>(() => readStoredAddSearchView());
  const [showAllResults, setShowAllResults] = useState(false);
  const [lastAdd, setLastAdd] = useState<LastAdd | null>(null);
  const lastAddRef = useRef<LastAdd | null>(null);
  const addKeyRef = useRef(0);
  const gridRef = useRef<HTMLDivElement>(null);
  // Force "specific" mode for Have List, otherwise default to "any"
  const [searchMode, setSearchMode] = useState<'any' | 'specific'>(isHaveList ? 'specific' : 'any');
  const [showImportModal, setShowImportModal] = useState(false);
  // Which importer option opens preselected; only the TopDecked deep link sets it.
  const [importInitialMode, setImportInitialMode] = useState<ImportInputMode>('paste');
  const [showAddSetModal, setShowAddSetModal] = useState(false);
  const [showBulkAlertsModal, setShowBulkAlertsModal] = useState(false);
  const [showImportMenu, setShowImportMenu] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Honor `?openBulkManage=true` so the SMS Alerts launch announcement can
  // chain into bulk-manage after navigating to Have List. We strip the param
  // immediately so a refresh doesn't re-open the modal.
  useEffect(() => {
    if (!isHaveList) return;
    if (searchParams.get('openBulkManage') !== 'true') return;
    setShowBulkAlertsModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete('openBulkManage');
    setSearchParams(next, { replace: true });
  }, [isHaveList, searchParams, setSearchParams]);

  // Honor `?import=topdecked` (the /topdecked landing page call to action) by opening
  // the importer with the TopDecked option preselected. Same strip-on-arrival pattern
  // as openBulkManage; the param is not carried across tab switches (AddCards strips it
  // before any switch, so it does not belong in CARRIED_TAB_PARAMS).
  useEffect(() => {
    if (!isHaveList) return;
    if (searchParams.get('import') !== 'topdecked') return;
    setImportInitialMode('topdecked');
    setShowImportModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete('import');
    setSearchParams(next, { replace: true });
  }, [isHaveList, searchParams, setSearchParams]);
  const [isModeSelectorOpen, setIsModeSelectorOpen] = useState(false);
  const [isViewSelectorOpen, setIsViewSelectorOpen] = useState(false);
  const [isSortSelectorOpen, setIsSortSelectorOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const modeSelectorRef = useRef<HTMLDivElement>(null);
  const viewSelectorRef = useRef<HTMLDivElement>(null);
  const sortSelectorRef = useRef<HTMLDivElement>(null);
  const tradeFilterRef = useRef<HTMLDivElement>(null);
  const [isTradeFilterOpen, setIsTradeFilterOpen] = useState(false);

  // Get current user's username and custom slug for share functionality
  const username = useSelector((state: RootState) => state.auth.user?.username);
  const { data: meData } = useGetMeQuery();
  
  // Regular search for "Specific Set" mode. While a set is locked, a collector
  // number in the main search looks that number up inside the lock.
  const specificArgs = buildAddSearchArgs({
    search: debouncedSearch,
    filterText: debouncedFilter,
    lockedFilter,
    searchAll: searchAllOnce,
  });
  const {
    data: specificData,
    hasNextPage: specificHasNextPage,
    isFetching: specificIsFetching,
    isFetchingNextPage: specificIsFetchingNextPage,
    fetchNextPage: specificFetchNextPage
  } = useCardsSearchInfiniteQuery(
    specificArgs,
    { skip: addSearchIsIdle(specificArgs) || searchMode !== 'specific' }
  );
  const specificTotalCount = specificData?.pages?.[0]?.totalCount ?? 0;
  // Keep the filter row mounted while the user is actively filtering, even if
  // their typing narrows totalCount to <=20. We check `filterText` (the immediate
  // input value) NOT `debouncedFilter` (the debounced value sent to the backend):
  // during the 300ms debounce gap `debouncedFilter` is still stale, so using it
  // here would let the row unmount between a keystroke and the debounce firing —
  // dropping keyboard focus mid-typing. Do not "simplify" this to debouncedFilter.
  // The reset paths (clear/Escape, primary search change, card select, mode
  // switch, dropdown close) all clear `filterText`, which lets the row unmount
  // again once the user is done.
  // Fast Entry: the row holds the set lock and the List / Image switch, so it is
  // always there for a Specific Set search (the old "more than 20 printings"
  // threshold would have hidden the lock on a card with few printings).
  const shouldShowFilterRow = searchMode === 'specific';
  
  // Consolidated search for "Any Set" mode
  const { 
    data: consolidatedData, 
    hasNextPage: consolidatedHasNextPage, 
    isFetching: consolidatedIsFetching, 
    isFetchingNextPage: consolidatedIsFetchingNextPage, 
    fetchNextPage: consolidatedFetchNextPage 
  } = useSearchConsolidatedInfiniteQuery(
    { name: debouncedSearch },
    { skip: !debouncedSearch || searchMode !== 'any' }
  );
  
  // Use the appropriate data based on search mode
  const isFetching = searchMode === 'any' ? consolidatedIsFetching : specificIsFetching;
  const isFetchingNextPage = searchMode === 'any' ? consolidatedIsFetchingNextPage : specificIsFetchingNextPage;
  const hasNextPage = searchMode === 'any' ? consolidatedHasNextPage : specificHasNextPage;
  const fetchNextPage = searchMode === 'any' ? consolidatedFetchNextPage : specificFetchNextPage;
  
  const isInitialLoading = isFetching && !isFetchingNextPage;
  const allCards: (ConsolidatedCard | MTGCard)[] | undefined = useMemo(() => {
    if (isInitialLoading) return undefined;
    if (searchMode === 'any') {
      return consolidatedData?.pages.flatMap((page) => page.cards) || [];
    } else {
      const cards = specificData?.pages.flatMap((page) => page.cards) || [];
      // Sort alphabetically by set name (A-Z)
      return [...cards].sort((a, b) => (a.setName || '').localeCompare(b.setName || ''));
    }
  }, [isInitialLoading, searchMode, consolidatedData, specificData]);

  // Debounced handler (memoized once)
  const debouncedUpdateSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
        scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedUpdateSearch.cancel();
    };
  }, [debouncedUpdateSearch]);

  // Debounced printing filter handler (only used when searchMode === 'specific'
  // and totalCount > 20). Sends a `filter` query param to /cards/search-light.
  const debouncedUpdateFilter = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedFilter(value);
        scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedUpdateFilter.cancel();
    };
  }, [debouncedUpdateFilter]);

  // Handle clicks outside menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowImportMenu(false);
      }
    };

    if (showImportMenu && active) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showImportMenu, active]);

  // Handle clicks outside mode selector to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modeSelectorRef.current && !modeSelectorRef.current.contains(event.target as Node)) {
        setIsModeSelectorOpen(false);
      }
    };

    if (isModeSelectorOpen && active) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModeSelectorOpen, active]);

  // Handle clicks outside view selector to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (viewSelectorRef.current && !viewSelectorRef.current.contains(event.target as Node)) {
        setIsViewSelectorOpen(false);
      }
    };

    if (isViewSelectorOpen && active) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isViewSelectorOpen, active]);

  // Handle clicks outside sort selector to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortSelectorRef.current && !sortSelectorRef.current.contains(event.target as Node)) {
        setIsSortSelectorOpen(false);
      }
    };

    if (isSortSelectorOpen && active) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSortSelectorOpen, active]);

  // Handle clicks outside trade filter to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tradeFilterRef.current && !tradeFilterRef.current.contains(event.target as Node)) {
        setIsTradeFilterOpen(false);
      }
    };

    if (isTradeFilterOpen && active) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTradeFilterOpen, active]);

  // Mode options for custom dropdown
  const MODE_OPTIONS = [
    { value: 'any' as const, label: 'Any Set', iconUrl: 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/infinity.svg' },
    { value: 'specific' as const, label: 'Specific Set', iconUrl: 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/one.svg' },
  ];

  // View options for custom dropdown
  const VIEW_OPTIONS = [
    { value: 'list' as const, label: 'List View' },
    { value: 'comfortable' as const, label: 'Image View' },
    { value: 'compact' as const, label: 'Images - Compact' },
  ];

  // Sort options for custom dropdown
  // Each option encodes sort key and direction as "key-direction"
  const SORT_OPTIONS = [
    { value: 'created_at-desc', label: 'Date Added' },
    { value: 'updated_at-desc', label: 'Date Updated' },
    { value: 'reference_price-asc', label: 'Price (Low to High)' },
    { value: 'reference_price-desc', label: 'Price (High to Low)' },
    { value: 'card_name-asc', label: 'Alphabetical' },
    // Bucket sorts (labels fixed by the filter-rail spec, decision 7).
    { value: 'color_identity-asc', label: 'Color' },
    { value: 'set_released-asc', label: 'Set (Oldest First)' },
    { value: 'set_released-desc', label: 'Set (Newest First)' },
  ];

  // Trade filter options
  const TRADE_FILTER_OPTIONS = [
    { value: 'all' as const, label: 'All Cards' },
    { value: 'forTrade' as const, label: 'For Trade' },
    { value: 'notForTrade' as const, label: 'Not for Trade' },
  ];

  const currentTradeFilterLabel = TRADE_FILTER_OPTIONS.find(o => o.value === (tradeFilter || 'all'))?.label || 'All Cards';

  // Get the current sort compound value for display
  const currentSortValue = `${orderBy || 'created_at'}-${order || 'desc'}`;
  const currentSortLabel = SORT_OPTIONS.find(o => o.value === currentSortValue)?.label || 'Date Added';

  // Handle sort option selection
  const handleSortSelect = useCallback((compoundValue: string) => {
    const [sortKey, direction] = compoundValue.split('-');
    if (onSortChange) {
      onSortChange(sortKey, direction as SortOrder);
    }
    setIsSortSelectorOpen(false);
  }, [onSortChange]);

  const handleSearchChange = useCallback(
    (text: string) => {
      setSearch(text);
      debouncedUpdateSearch(text);
      setSelectedIndex(-1); // Reset selection when search changes
      setSearchAllOnce(false);
      setShowAllResults(false);
      // A locked set carries over to every new search; otherwise the filter resets.
      if (lockedFilter === null) {
        setFilterText('');
        setDebouncedFilter('');
        debouncedUpdateFilter.cancel();
      }
    },
    [debouncedUpdateSearch, debouncedUpdateFilter, lockedFilter]
  );

  const lockFilter = useCallback(() => {
    const text = filterText.trim();
    if (!text) return;
    setLockedFilter(text);
    setDebouncedFilter(text);
    debouncedUpdateFilter.cancel();
  }, [filterText, debouncedUpdateFilter]);

  const unlockFilter = useCallback(() => setLockedFilter(null), []);

  /** Clear what an add clears: the search, the highlight, and the filter unless it is locked. */
  const resetAfterAdd = useCallback(() => {
    setSearch('');
    setDebouncedSearch('');
    setSelectedIndex(-1);
    setSearchAllOnce(false);
    setShowAllResults(false);
    if (lockedFilter === null) {
      setFilterText('');
      setDebouncedFilter('');
      debouncedUpdateFilter.cancel();
    }
  }, [lockedFilter, debouncedUpdateFilter]);

  /**
   * One add path for Enter and click (both modes): hands the printing to the
   * owner, then shows the confirmation with the owner's undo when it offers one.
   */
  const commitAdd = useCallback(
    async (item: ConsolidatedCard | MTGCard) => {
      let result: AddCardResult | void;
      if (searchMode === 'any') {
        const consolidatedCard = item as ConsolidatedCard;
        result = await onCardAdd(consolidatedCard.oracleId, consolidatedCard, searchMode);
      } else {
        const mtgCard = item as MTGCard;
        const identifier = useOracleId && mtgCard.oracleId ? mtgCard.oracleId : mtgCard.id;
        result = await onCardAdd(identifier, mtgCard, searchMode);
      }
      if (result && typeof result.undo === 'function') {
        const mtgCard = item as MTGCard;
        const name = searchMode === 'any' ? (item as ConsolidatedCard).cardName : mtgCard.name;
        const printing = searchMode === 'any'
          ? ''
          : `${(mtgCard.setCode || '').toUpperCase()} #${mtgCard.collectorNumber || (mtgCard as any).collector_number || ''}`;
        const settings = searchMode === 'any' ? undefined : entryDefaults?.confirmationLine?.(mtgCard);
        const thumbUrl = searchMode === 'any'
          ? (item as ConsolidatedCard).sampleImage?.small || (item as ConsolidatedCard).sampleImage?.art_crop || ''
          : getCardImageUrl(cardImageInputsFromCard(mtgCard), 'small');
        const add: LastAdd = {
          key: ++addKeyRef.current,
          cardName: name,
          detailLine: [printing, settings].filter(Boolean).join(' · '),
          thumbUrl,
          undo: result.undo,
        };
        lastAddRef.current = add;
        setLastAdd(add);
      }
    },
    [onCardAdd, searchMode, useOracleId, entryDefaults],
  );

  const undoLastAdd = useCallback(async () => {
    const add = lastAddRef.current;
    if (!add) return;
    lastAddRef.current = null;
    setLastAdd(null);
    try {
      await add.undo();
    } catch (error) {
      console.error('Undo failed:', error);
      toast.error("Couldn't undo that add. Please check your list.");
    }
  }, []);

  const expireLastAdd = useCallback(() => {
    lastAddRef.current = null;
    setLastAdd(null);
  }, []);

  const changeResultsView = useCallback((view: AddSearchView) => {
    setResultsView(view);
    storeAddSearchView(view);
    setSelectedIndex(-1);
  }, []);

  /** Image mode columns, from the rendered grid, so Up/Down move by one row. */
  const gridColumns = useCallback((): number => {
    const grid = gridRef.current;
    if (!grid || grid.children.length === 0) return 1;
    const firstTop = (grid.children[0] as HTMLElement).offsetTop;
    let columns = 0;
    for (const child of Array.from(grid.children)) {
      if ((child as HTMLElement).offsetTop !== firstTop) break;
      columns += 1;
    }
    return Math.max(1, columns);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // A hidden tab's Add box ignores every key: inert covers the browser, this
      // covers jsdom and synthetic events (Ctrl/Cmd+Z must never undo an add
      // made on the other tab).
      if (!active) return;
      // Shift+Enter: search every set for this one search; the lock stays on.
      if (e.key === 'Enter' && e.shiftKey && lockedFilter !== null) {
        e.preventDefault();
        setSearchAllOnce(true);
        setSelectedIndex(-1);
        return;
      }
      // Ctrl/Cmd+Z with an empty search undoes the last add.
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && search === '') {
        if (lastAddRef.current) {
          e.preventDefault();
          void undoLastAdd();
        }
        return;
      }
      if (!allCards || allCards.length === 0) return;
      const count = Math.min(allCards.length, showAllResults ? allCards.length : IMAGE_RESULTS_LIMIT);
      const scrollTo = (index: number) => {
        if (!scrollRef.current || index < 0) return;
        const items = scrollRef.current.querySelectorAll(
          resultsView === 'image' ? '.add-cards-image-grid__item' : '.add-cards-search-list-item',
        );
        items[index]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      };
      const step = resultsView === 'image' && searchMode === 'specific' ? gridColumns() : 1;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => {
            const newIndex = prev === -1 ? 0 : Math.min(prev + step, count - 1);
            scrollTo(newIndex);
            return newIndex;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => {
            const newIndex = prev - step < 0 ? -1 : prev - step;
            scrollTo(newIndex);
            return newIndex;
          });
          break;
        case 'ArrowRight':
          if (resultsView !== 'image' || searchMode !== 'specific') break;
          e.preventDefault();
          setSelectedIndex((prev) => {
            const newIndex = prev === -1 ? 0 : Math.min(prev + 1, count - 1);
            scrollTo(newIndex);
            return newIndex;
          });
          break;
        case 'ArrowLeft':
          if (resultsView !== 'image' || searchMode !== 'specific') break;
          e.preventDefault();
          setSelectedIndex((prev) => {
            const newIndex = Math.max(prev - 1, 0);
            scrollTo(newIndex);
            return newIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < count) {
            const selectedCard = allCards[selectedIndex];
            void commitAdd(selectedCard);
            resetAfterAdd();
            // Keep the cursor in the primary search input so the user can
            // immediately type the next card without re-clicking. Clearing
            // `search` unmounts the dropdown overlay (and the filter-row input,
            // which shares this keydown handler), so focus would otherwise fall
            // back to <body>. The input element itself is always mounted, so a
            // synchronous focus() is safe here.
            searchInputRef.current?.focus();
          }
          break;
        case 'Escape':
          e.preventDefault();
          if (filterText && lockedFilter === null) {
            setFilterText('');
            setDebouncedFilter('');
            debouncedUpdateFilter.cancel();
            setSelectedIndex(-1);
          } else {
            setSearch('');
            setDebouncedSearch('');
            setSelectedIndex(-1);
          }
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [active, allCards, selectedIndex, searchMode, filterText, debouncedUpdateFilter, lockedFilter, search, showAllResults, resultsView, commitAdd, resetAfterAdd, undoLastAdd, gridColumns],
  );

  // Show the hovered printing's art in a fixed spot to the right of the list.
  // Anchored to the overlay rect (stable across rows) so the preview stays put
  // and only its art swaps as the cursor moves between rows. A short hover-intent
  // delay avoids flicker when the cursor passes over rows en route to a click.
  const handleRowHover = useCallback((preview: HoverPreview) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      const overlay = overlayRef.current;
      if (!overlay) return;
      const rect = overlay.getBoundingClientRect();
      // Suppress when there's no room for a right-side preview (narrow/mobile)
      // so it never overlaps the clickable rows.
      if (window.innerWidth - rect.right < 256) {
        setHoveredPreview(null);
        return;
      }
      setPreviewPos({ x: rect.right, y: rect.top });
      setHoveredPreview(preview);
    }, 120);
  }, []);

  const handleRowHoverEnd = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setHoveredPreview(null);
  }, []);

  // Clear any pending timer on unmount.
  useEffect(() => () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, []);

  // Drop a stale preview when the results dropdown closes.
  useEffect(() => {
    if (!(search && debouncedSearch)) handleRowHoverEnd();
  }, [search, debouncedSearch, handleRowHoverEnd]);

  const focusSearchInput = useCallback(() => searchInputRef.current?.focus(), []);
  // Card name takes focus on mount (the old autoFocus) and again each time this
  // panel is shown; a hidden tab never takes focus (Decision 2). Keyed on the
  // shown flag, not the route flag: focusing an input still at display none
  // does nothing.
  const visible = useTabPanelVisible();
  useEffect(() => {
    if (visible) searchInputRef.current?.focus();
  }, [visible]);

  const renderItem = (item: ConsolidatedCard | MTGCard, index: number) => {
    const isSelected = index === selectedIndex;
    const additionalStyle = index % 2 === 0 ? '' : 'black';
    const onPress = () => {
      void commitAdd(item);
      resetAfterAdd();
      handleRowHoverEnd();
      // Same as the Enter path: keep the cursor in Card name for the next card.
      searchInputRef.current?.focus();
    };

    return (
      <AddCardsSearchListItem
        key={searchMode === 'any'
          ? `add-cards-search-item-oracle-${(item as ConsolidatedCard).oracleId}`
          : `add-cards-search-item-id-${(item as MTGCard).id}`}
        item={item}
        additionalStyle={`${additionalStyle} ${isSelected ? 'selected' : ''}`}
        onPress={onPress}
        mode={searchMode}
        previewLine={searchMode === 'specific' ? entryDefaults?.previewLine?.(item as MTGCard) : undefined}
        onHover={(preview) => {
          // Hovering a result moves the keyboard highlight to it (Decision 10).
          setSelectedIndex(index);
          handleRowHover(preview);
        }}
        onHoverEnd={handleRowHoverEnd}
      />
    );
  };

  const onListEndReached = useCallback(() => {
    // The first IMAGE_RESULTS_LIMIT printings render until "Show all"; more
    // pages are fetched only once the user asked for all of them.
    if (!showAllResults) return;
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, showAllResults]);

  const visibleCards = useMemo(
    () => (allCards && !showAllResults ? allCards.slice(0, IMAGE_RESULTS_LIMIT) : allCards),
    [allCards, showAllResults],
  );
  const hiddenPrintings = allCards ? Math.max(0, specificTotalCount - Math.min(allCards.length, IMAGE_RESULTS_LIMIT)) : 0;
  const showShowAll = searchMode === 'specific' && !showAllResults && !!allCards && (hiddenPrintings > 0 || hasNextPage);
  const lockedEmpty = searchMode === 'specific' && lockedFilter !== null && !searchAllOnce && !!allCards && allCards.length === 0 && !isInitialLoading;

  const renderImageItem = (item: MTGCard, index: number) => {
    const inputs = cardImageInputsFromCard(item);
    const collectorNumber = item.collectorNumber || (item as any).collector_number;
    const line = entryDefaults?.previewLine?.(item);
    return (
      <button
        type="button"
        key={`add-cards-image-item-${item.id}`}
        className={`add-cards-image-grid__item${index === selectedIndex ? ' selected' : ''}`}
        onClick={() => {
          void commitAdd(item);
          resetAfterAdd();
          searchInputRef.current?.focus();
        }}
        onMouseEnter={() => setSelectedIndex(index)}
        aria-label={`Add ${item.name}, ${(item.setCode || '').toUpperCase()} #${collectorNumber || ''}`}
      >
        <img
          className="add-cards-image-grid__card"
          src={getCardImageUrl(inputs, 'small')}
          alt=""
          width={146}
          height={204}
          loading="lazy"
        />
        <span className="add-cards-image-grid__caption">
          {(item.setCode || '').toUpperCase()} #{collectorNumber}
        </span>
        {line && line.startsWith('No ') && <span className="add-cards-image-grid__note">{line}</span>}
      </button>
    );
  };

  // Handle share functionality
  const handleShare = async () => {
    if (!username) {
      toast.error('Unable to share: User not found');
      return;
    }

    const tab = isHaveList ? 'have-list' : (isWantList ? 'want-list' : 'have-list');
    const basePath = meData?.customSlug
      ? `https://trademagic.gg/u/${meData.customSlug}`
      : `https://trademagic.gg/profile/u/${username}`;
    const shareUrl = `${basePath}?tab=${tab}`;

    try {
      if (!(await copyToClipboard(shareUrl))) {
        toast.error('Failed to copy link');
        return;
      }
      toast.success('Link copied to clipboard!');
      setShowImportMenu(false);
    } catch (err) {
      console.error('Failed to copy link:', err);
      toast.error('Failed to copy link');
    }
  };

  // Safe subtitle rendering component
  const renderSubtitle = () => {
    if (!subtitle) return null;

    // Check if subtitle contains "send page" text to replace with link
    if (subtitle.includes('send page')) {
      // Split the text around "send page"
      const parts = subtitle.split('send page');

      return (
        <div className="add-cards-subtitle">
          {parts[0]}
          <Link to="/sendcards">send page</Link>
          {parts[1]}
        </div>
      );
    }

    // For other subtitles, render as plain text
    return <div className="add-cards-subtitle">{subtitle}</div>;
  };

  return (
    <>
      <div className="add-cards-header">
        <div className="add-cards-title">
          <span>{title}</span>
        </div>
        {renderSubtitle()}
      </div>
      <div className={`add-cards-controls add-cards-controls--collection ${isHaveList ? 'add-cards-controls--have' : ''}`}>
        <div className={`add-cards-search-input ${search && debouncedSearch ? 'with-dropdown' : ''}`}>
          <TextInputField
            leftComponent={
              <FontAwesomeIcon
                size="sm"
                icon={'plus'}
                color="#DD5BEA"
              />
            }
            value={search}
            placeholder={lockedFilter !== null ? `Card name or number in ${lockedFilter.toUpperCase()}` : placeholder}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            inputRef={searchInputRef}
            rightComponent={entryDefaults?.renderPill(focusSearchInput)}
          />
          {search && debouncedSearch && (
            <div
              className="add-cards-list-overlay"
              ref={overlayRef}
              style={overlayMaxHeight !== null ? { maxHeight: overlayMaxHeight } : undefined}
            >
              {shouldShowFilterRow && (
                <div className="add-cards-filter-row">
                  <span
                    className="add-cards-filter-row__badge"
                    aria-live="polite"
                  >
                    {specificTotalCount} printings
                  </span>
                  <div className={`add-cards-filter-row__field${lockedFilter !== null ? ' add-cards-filter-row__field--locked' : ''}`}>
                    <input
                      ref={filterInputRef}
                      className="add-cards-filter-row__input"
                      type="text"
                      value={filterText}
                      placeholder="Filter by set name, code, or collector number"
                      aria-label="Filter results by set, code or collector number"
                      onChange={(e) => {
                        const next = e.target.value;
                        setFilterText(next);
                        setSelectedIndex(-1);
                        setSearchAllOnce(false);
                        // Clearing the text also unlocks (Decision 5).
                        if (!next.trim()) {
                          setLockedFilter(null);
                          debouncedUpdateFilter(next);
                        } else if (lockedFilter !== null) {
                          // Editing a locked filter unlocks it; the new text applies after the debounce.
                          setLockedFilter(null);
                          debouncedUpdateFilter(next);
                        } else {
                          debouncedUpdateFilter(next);
                        }
                      }}
                      onKeyDown={handleKeyDown}
                    />
                    {filterText.trim() !== '' && (
                      <button
                        type="button"
                        className={`add-cards-filter-lock${lockedFilter !== null ? ' add-cards-filter-lock--on' : ''}`}
                        onClick={() => (lockedFilter !== null ? unlockFilter() : lockFilter())}
                        aria-pressed={lockedFilter !== null}
                        aria-label={lockedFilter !== null ? 'Locked' : 'Lock set'}
                        title={lockedFilter !== null ? 'Unlock set' : 'Lock set for the next searches'}
                      >
                        <FontAwesomeIcon icon={lockedFilter !== null ? faLock : faLockOpen} />
                        <span>{lockedFilter !== null ? 'Locked' : 'Lock set'}</span>
                      </button>
                    )}
                  </div>
                  <SegmentedToggle<AddSearchView>
                    className="add-cards-view-switch"
                    ariaLabel="Results layout"
                    value={resultsView}
                    onChange={changeResultsView}
                    options={[
                      { value: 'list', label: 'List' },
                      { value: 'image', label: 'Image' },
                    ]}
                  />
                </div>
              )}
              {lockedEmpty ? (
                <div className="add-cards-locked-empty" data-testid="add-cards-locked-empty">
                  <p className="add-cards-locked-empty__title">
                    No &quot;{debouncedSearch}&quot; printings in &quot;{lockedFilter}&quot;
                  </p>
                  <p className="add-cards-locked-empty__body">Nothing is added from another set unless you choose it.</p>
                  <button
                    type="button"
                    className="add-cards-locked-empty__button"
                    onClick={() => {
                      setSearchAllOnce(true);
                      searchInputRef.current?.focus();
                    }}
                  >
                    Search all sets <kbd>Shift + Enter</kbd>
                  </button>
                </div>
              ) : searchMode === 'specific' && resultsView === 'image' ? (
                <div
                  ref={scrollRef}
                  className="add-cards-image-scroll"
                  onScroll={(e) => {
                    const el = e.currentTarget;
                    if ((el.scrollTop + el.clientHeight) / el.scrollHeight >= 0.95) onListEndReached();
                  }}
                >
                  {isInitialLoading ? (
                    <p className="add-cards-image-grid__status">Loading…</p>
                  ) : !visibleCards || visibleCards.length === 0 ? (
                    <p className="add-cards-image-grid__status">No cards found</p>
                  ) : (
                    <div className="add-cards-image-grid" ref={gridRef} data-testid="add-cards-image-grid">
                      {(visibleCards as MTGCard[]).map(renderImageItem)}
                    </div>
                  )}
                </div>
              ) : (
                <InfiniteScroll
                  ref={scrollRef}
                  data={visibleCards}
                  renderItem={renderItem}
                  onEndReached={onListEndReached}
                  isLoading={isInitialLoading}
                  isLoadingMore={isFetchingNextPage}
                  emptyListMessage="No cards found"
                />
              )}
              {showShowAll && (
                <button
                  type="button"
                  className="add-cards-show-all"
                  onClick={() => setShowAllResults(true)}
                >
                  Show all {specificTotalCount.toLocaleString()} printings
                </button>
              )}
              {searchMode === 'specific' && (
                <div className="add-cards-key-hints" aria-hidden="true">
                  <span><kbd>↑</kbd> <kbd>↓</kbd> Move</span>
                  <span><kbd>Enter</kbd> Add</span>
                  <span><kbd>Esc</kbd> Clear</span>
                  <span><kbd>Ctrl/⌘ + Z</kbd> Undo</span>
                </div>
              )}
              {hoveredPreview && (
                <CardImageTooltip
                  scryfallId={hoveredPreview.scryfallId}
                  imageUrl={hoveredPreview.imageUrl}
                  normalFallbacks={hoveredPreview.normalFallbacks}
                  cardName={hoveredPreview.cardName}
                  position={previewPos}
                  onMouseEnter={() => {}}
                  onMouseLeave={() => setHoveredPreview(null)}
                />
              )}
            </div>
          )}
        </div>
        {/* The Have List is always Specific Set, so it has no Printing selector at all. */}
        {!isHaveList && (
        <div
          className="add-cards-mode-selector-custom"
          ref={modeSelectorRef}
        >
          <span className="add-cards-dropdown-label">Printing</span>
          <button
            type="button"
            className={`add-cards-mode-trigger ${isModeSelectorOpen ? 'open' : ''}`}
            onClick={() => setIsModeSelectorOpen(!isModeSelectorOpen)}
            aria-haspopup="listbox"
            aria-expanded={isModeSelectorOpen}
            aria-label="Select search mode"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsModeSelectorOpen(false);
              }
            }}
          >
            <span
              className="add-cards-mode-icon"
              style={{
                WebkitMask: `url(${MODE_OPTIONS.find(o => o.value === searchMode)?.iconUrl}) no-repeat center`,
                mask: `url(${MODE_OPTIONS.find(o => o.value === searchMode)?.iconUrl}) no-repeat center`
              }}
            />
            <span className="add-cards-mode-label">
              {MODE_OPTIONS.find(o => o.value === searchMode)?.label}
            </span>
            <svg
              className={`add-cards-mode-chevron ${isModeSelectorOpen ? 'open' : ''}`}
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {isModeSelectorOpen && (
            <div className="add-cards-mode-dropdown" role="listbox">
              {MODE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`add-cards-mode-option ${searchMode === option.value ? 'selected' : ''}`}
                  onClick={() => {
                    setSearchMode(option.value);
                    setSelectedIndex(-1);
                    setFilterText('');
                    setDebouncedFilter('');
                    debouncedUpdateFilter.cancel();
                    setIsModeSelectorOpen(false);
                  }}
                  role="option"
                  aria-selected={searchMode === option.value}
                >
                  <span
                    className="add-cards-mode-icon"
                    style={{
                      WebkitMask: `url(${option.iconUrl}) no-repeat center`,
                      mask: `url(${option.iconUrl}) no-repeat center`
                    }}
                  />
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        )}
        <div
          className="add-cards-view-selector-custom"
          ref={viewSelectorRef}
        >
          <span className="add-cards-dropdown-label">View</span>
          <button
            type="button"
            className={`add-cards-view-trigger ${isViewSelectorOpen ? 'open' : ''}`}
            onClick={() => setIsViewSelectorOpen(!isViewSelectorOpen)}
            aria-haspopup="listbox"
            aria-expanded={isViewSelectorOpen}
            aria-label="Select view mode"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsViewSelectorOpen(false);
              }
            }}
          >
            <span className="add-cards-view-label">
              {VIEW_OPTIONS.find(o => o.value === viewMode)?.label}
            </span>
            <svg
              className={`add-cards-view-chevron ${isViewSelectorOpen ? 'open' : ''}`}
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {isViewSelectorOpen && (
            <div className="add-cards-view-dropdown" role="listbox">
              {VIEW_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`add-cards-view-option ${viewMode === option.value ? 'selected' : ''}`}
                  onClick={() => {
                    onViewModeChange?.(option.value);
                    setIsViewSelectorOpen(false);
                  }}
                  role="option"
                  aria-selected={viewMode === option.value}
                >
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {onSortChange && (
          <div
            className="add-cards-sort-selector-custom"
            ref={sortSelectorRef}
          >
            <span className="add-cards-dropdown-label">Sort</span>
            <button
              type="button"
              className={`add-cards-sort-trigger ${isSortSelectorOpen ? 'open' : ''}`}
              onClick={() => setIsSortSelectorOpen(!isSortSelectorOpen)}
              aria-haspopup="listbox"
              aria-expanded={isSortSelectorOpen}
              aria-label="Select sort order"
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsSortSelectorOpen(false);
                }
              }}
            >
              <span className="add-cards-sort-label">
                {currentSortLabel}
              </span>
              <svg
                className={`add-cards-sort-chevron ${isSortSelectorOpen ? 'open' : ''}`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {isSortSelectorOpen && (
              <div className="add-cards-sort-dropdown" role="listbox">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`add-cards-sort-option ${currentSortValue === option.value ? 'selected' : ''}`}
                    onClick={() => handleSortSelect(option.value)}
                    role="option"
                    aria-selected={currentSortValue === option.value}
                  >
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {isHaveList && onTradeFilterChange && (
          <div className="add-cards-trade-filter-custom" ref={tradeFilterRef}>
            <span className="add-cards-dropdown-label">Trade</span>
            <button
              type="button"
              className="add-cards-trade-filter-trigger"
              onClick={() => setIsTradeFilterOpen(!isTradeFilterOpen)}
              aria-haspopup="listbox"
              aria-expanded={isTradeFilterOpen}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsTradeFilterOpen(false);
                }
              }}
            >
              <span className="add-cards-trade-filter-label">
                {currentTradeFilterLabel}
              </span>
              <svg
                className={`add-cards-sort-chevron ${isTradeFilterOpen ? 'open' : ''}`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {isTradeFilterOpen && (
              <div className="add-cards-trade-filter-dropdown" role="listbox">
                {TRADE_FILTER_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`add-cards-trade-filter-option ${(tradeFilter || 'all') === option.value ? 'selected' : ''}`}
                    onClick={() => {
                      onTradeFilterChange(option.value);
                      setIsTradeFilterOpen(false);
                    }}
                    role="option"
                    aria-selected={(tradeFilter || 'all') === option.value}
                  >
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {onSearchClick && (
          <button
            type="button"
            className="add-cards-search-button"
            onClick={onSearchClick}
            aria-label="Search cards by name"
            title="Search cards by name"
          >
            <FontAwesomeIcon icon="magnifying-glass" size="sm" />
          </button>
        )}
        <div className="add-cards-import-menu" ref={menuRef}>
          <button
            className="add-cards-import-btn"
            onClick={() => setShowImportMenu(!showImportMenu)}
            aria-label="More options"
          >
            <img
              src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/more.svg"
              alt="More options"
              className="add-cards-import-icon"
            />
          </button>
          {showImportMenu && (
            <div className="add-cards-import-dropdown">
              <button
                className="add-cards-import-option"
                onClick={() => {
                  setShowImportModal(true);
                  setShowImportMenu(false);
                }}
              >
                <FontAwesomeIcon icon="file-import" style={{ marginRight: '8px' }} />
                Import
              </button>
              {onExportCsv && (
                <button
                  className="add-cards-import-option"
                  onClick={() => {
                    onExportCsv();
                    setShowImportMenu(false);
                  }}
                  disabled={isExporting}
                >
                  <FontAwesomeIcon icon="file-export" style={{ marginRight: '8px' }} />
                  {isExporting ? 'Exporting...' : 'Export'}
                </button>
              )}
              {(isWantList || isHaveList) && (
                <button
                  className="add-cards-import-option"
                  onClick={() => {
                    setShowAddSetModal(true);
                    setShowImportMenu(false);
                  }}
                >
                  <FontAwesomeIcon icon="circle-plus" style={{ marginRight: '8px' }} />
                  Add Set
                </button>
              )}
              <button
                className="add-cards-import-option"
                onClick={handleShare}
              >
                <FontAwesomeIcon icon="share" style={{ marginRight: '8px' }} />
                Share
              </button>

              {isHaveList && (
                <button
                  className="add-cards-import-option"
                  onClick={() => {
                    setShowBulkAlertsModal(true);
                    setShowImportMenu(false);
                  }}
                >
                  <FontAwesomeIcon icon="bell" style={{ marginRight: '8px' }} />
                  Alerts
                </button>
              )}

              {onDeleteAll && (
                <button
                  className="add-cards-delete-option"
                  onClick={() => {
                    onDeleteAll();
                    setShowImportMenu(false);
                  }}
                >
                  <FontAwesomeIcon icon="trash" style={{ marginRight: '8px' }} />
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <AddConfirmation add={lastAdd} onUndo={() => void undoLastAdd()} onExpire={expireLastAdd} />
      {showImportModal && (
        <ImportCardsModal
          isOpen={showImportModal}
          onClose={() => {
            setShowImportModal(false);
            setImportInitialMode('paste');
          }}
          onCardAdd={onCardAdd}
          useOracleId={useOracleId}
          isHaveList={isHaveList}
          initialInputMode={importInitialMode}
        />
      )}
      {(isWantList || isHaveList) && (
        <AddSetModal
          isOpen={showAddSetModal}
          onClose={() => setShowAddSetModal(false)}
          listType={isWantList ? 'want' : 'have'}
        />
      )}
      {isHaveList && (
        <BulkManageAlertsModal
          isOpen={showBulkAlertsModal}
          onClose={() => setShowBulkAlertsModal(false)}
        />
      )}
    </>
  );
};
